import { useMemo } from "react";
import { AppointmentReportGridContainerProps } from "../../AppointmentReport/AppointmentReportGridContainer";
import { Appointment } from "./types";
import {
  AppointmentReport,
  AiReportSection,
} from "../../AppointmentReport/AppointmentReportGrid/types";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { formatStudyNameWithSide } from "../../AppointmentOffering/utils/formatStudyNameWithSide";

const useAppointmentReportGridContainer = ({
  appointment,
  refetch,
}: {
  appointment: Appointment | null;
  refetch: () => Promise<void>;
}) => {
  const generateReportSignedUrl = useFirebaseAppFunction(
    "generateReportSignedUrl",
  );

  const props = useMemo<AppointmentReportGridContainerProps | null>(() => {
    if (!appointment) return null;

    const { appointment_reports, appointment_ai_reports, order_number } =
      appointment;

    const reports = appointment_reports.map<AppointmentReport>((report) => {
      const onDownloadClick = async () => {
        const {
          data: { downloadUrl },
        } = await generateReportSignedUrl({
          reportId: report.id,
        });

        window.open(downloadUrl);
      };

      return {
        id: report.id,
        name: report.filename,
        status: report.status,
        hasUnexpectedFindings: !!report.has_unexpected_findings,
        isReceiptConfirmed: !!report.is_receipt_confirmed,
        type: "pdf",
        onDownloadClick,
      };
    });

    if (appointment_ai_reports.length > 0) {
      const aiReport = appointment_ai_reports[0];

      const aiReportName = `report_summary_${order_number}`;

      const sections: AiReportSection[] = [];

      if (aiReport.summary_section) {
        sections.push({
          title: "summary",
          text: aiReport.summary_section,
        });
      }

      if (aiReport.findings_section) {
        try {
          const findings =
            typeof aiReport.findings_section === "string"
              ? JSON.parse(aiReport.findings_section)
              : aiReport.findings_section;
          sections.push({
            title: "findings",
            findings,
          });
        } catch (e) {
          // Error parsing findings_section
        }
      }

      if (aiReport.next_steps_section) {
        try {
          const nextSteps =
            typeof aiReport.next_steps_section === "string"
              ? JSON.parse(aiReport.next_steps_section)
              : aiReport.next_steps_section;
          sections.push({
            title: "nextSteps",
            nextSteps,
          });
        } catch (e) {
          // Error parsing next_steps_section
        }
      }

      reports.push({
        id: aiReport.id.toString(),
        name: aiReportName,
        status: aiReport.status || "",
        type: "ai",
        sections,
      });
    }

    const patient = appointment.appointment_patient_details[0];

    const packageStudyNames = appointment.appointment_packages.flatMap((p) =>
      p.appointment_offerings.map((o) =>
        formatStudyNameWithSide(o.center_offering.name, o.side),
      ),
    );

    const offeringStudyNames = appointment.appointment_offerings.map((o) =>
      formatStudyNameWithSide(o.center_offering.name, o.side),
    );

    const studyNames = [...packageStudyNames, ...offeringStudyNames];

    const appointmentAiReports = appointment_ai_reports.map((report) => ({
      id: report.id,
      status: report.status || "",
    }));

    return {
      reports,
      appointmentAiReports,
      patientFirstName: patient.first_name,
      patientLastName: patient.last_name,
      patientEmail: patient.email || "",
      appointmentId: appointment.id,
      locationCity: appointment.center.city,
      studyNames,
      appointmentDate: appointment.center_time_slot.started_at,
      refetch,
    };
  }, [appointment, generateReportSignedUrl, refetch]);

  return props;
};

export default useAppointmentReportGridContainer;
